import {
    type DragEvent,
    type FC,
    type ReactNode,
    useState,
} from "react";
import { ToastTypeEnum, useToastCreate } from "@onshopfront/components";
import { useShopfrontDispatch } from "../../../../_Bootstrap/hooks";
import { hideDialog } from "../../../../Actions/Dialogs/DialogActions";
import { startLoading, stopLoading } from "../../../../Actions/General/LayoutActions";
import Button from "../../../../Common/Buttons/Button";
import { determineDataSource } from "../../../../Data/DataStores/DataSource";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import {
    type BackupImportTuple,
    OfflineSaleBackupManager,
} from "../../../../Utilities/Platform/OfflineSaleBackupManager";

export const ImportSaleBackup: FC = () => {
    const [ dragOver, setDragOver ] = useState(false);
    const [ hasHandle, setHasHandle ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ sales, setSales ] = useState<Array<BackupImportTuple>>([]);
    const [ matched, setMatched ] = useState<Record<string, number>>({});

    const dispatch = useShopfrontDispatch();

    const createToast = useToastCreate();

    const onImport = async (handle: FileSystemDirectoryHandle): Promise<void> => {
        setHasHandle(true);
        setLoading(true);

        try {
            const sales = await OfflineSaleBackupManager.getInstance().importBackup(handle);

            const saleMap: Record<string, number> = {};

            for(let i = 0, l = sales.length; i < l; i++) {
                saleMap[sales[i][0].data] = i;
            }

            const source = await determineDataSource();

            const dbSales = await source.sales.all();

            // Check to see if the sales are already in the local db
            const matched: Record<string, number> = {};

            for(const dbSale of dbSales) {
                if(typeof saleMap[dbSale.id] !== "number") {
                    continue;
                }

                matched[dbSale.id] = saleMap[dbSale.id];
            }

            setMatched(matched);
            setSales(sales);
        } catch(err) {
            console.error("Unable to load sales backup", err);
            createToast({
                type   : ToastTypeEnum.Error,
                message: "Unable to parse the select backup files",
            });

            setHasHandle(false);
        }

        setLoading(false);
    };

    const onDrop = async (e: DragEvent<HTMLButtonElement>): Promise<void> => {
        // Check to see if the file is valid type
        const items = e.dataTransfer.items;

        if(items.length !== 1) {
            return;
        }

        const item = items[0];

        if(item.kind !== "file") {
            return;
        }

        // Validate imported file
        e.preventDefault();

        const handle = await item.getAsFileSystemHandle();

        if(!handle) {
            createToast({
                type   : ToastTypeEnum.Warning,
                message: "Unexpected item dropped.",
            });

            return;
        }

        if(handle.kind !== "directory") {
            createToast({
                type   : ToastTypeEnum.Warning,
                message: "Unexpected item dropped.",
            });

            return;
        }

        const permissions = await handle.requestPermission({
            mode: "read",
        });

        if(permissions !== "granted") {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "Unable to import when file permissions aren't granted.",
            });

            return;
        }

        await onImport(handle as FileSystemDirectoryHandle);
    };

    const onSelect = async (): Promise<void> => {
        const importHandle = await window.showDirectoryPicker();

        const permissions = await importHandle.requestPermission({
            mode: "read",
        });

        if(permissions !== "granted") {
            createToast({
                type   : ToastTypeEnum.Error,
                message: "Unable to import when file permissions aren't granted.",
            });

            return;
        }

        await onImport(importHandle);
    };

    const onFinaliseImport = async (ignore: boolean): Promise<void> => {
        if(!sales.length) {
            return;
        }

        dispatch(hideDialog());
        dispatch(startLoading());

        try {
            const source = await determineDataSource();

            for(const [ item, sale ] of sales) {
                if(ignore && matched[item.data]) {
                    continue;
                }

                await source.sales.put(sale);
                await source.uploads.put({
                    ...item,
                    status  : "queued",
                    attempts: 0,
                });
            }

            createToast({
                type   : ToastTypeEnum.Success,
                message: "Successfully imported backup sales.",
            });
        } catch(err) {
            console.error("Error importing backup sales", err);
            createToast({
                type   : ToastTypeEnum.Error,
                message: "Failed to import backup sales.",
            });
        }

        dispatch(stopLoading());
    };

    let content: ReactNode;

    if(!hasHandle) {
        content = (
            <>
                <button
                    onClick={onSelect}
                    onDragOver={e => {
                        e.preventDefault();

                        setDragOver(true);
                    }}
                    onDragLeave={() => {
                        setDragOver(false);
                    }}
                    onDragEnd={() => {
                        setDragOver(false);
                    }}
                    onDrop={onDrop}
                    className={
                        "border-2 border-dashed border-neutral-500 p-16 flex flex-col justify-center items-center " +
                        "bg-transparent cursor-pointer hover:border-neutral-600 hover:bg-neutral-100 w-[350px]"
                    }
                >
                    <FontAwesomeLight className="text-4xl text-neutral-500" icon="file-import" />
                    <div className="mt-2">
                        {
                            dragOver ?
                                "Drop file to import." :
                                "Drop or Select Directory for Import."
                        }
                    </div>
                </button>
                <div className="buttons">
                    <Button
                        classes={[ "button-cancel" ]}
                        onClick={() => {
                            dispatch(hideDialog());
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </>
        );
    } else if(loading) {
        content = (
            <>
                <div>
                    <FontAwesomeLight icon="spinner-third" spin /> Importing...
                </div>
                <div className="buttons">
                    <Button
                        classes={[ "button-cancel" ]}
                        onClick={() => {
                            dispatch(hideDialog());
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </>
        );
    } else if(!sales.length) {
        content = (
            <>
                <div>
                    No sales found in the import.
                </div>
                <div className="buttons">
                    <Button
                        classes={[ "button-cancel" ]}
                        onClick={() => {
                            dispatch(hideDialog());
                        }}
                    >
                        Okay
                    </Button>
                </div>
            </>
        );
    } else {
        const matchedCount = Object.keys(matched).length;

        content = (
            <div className="max-w-[500px]">
                <div>
                    {sales.length} sales found in backup.
                </div>
                {matchedCount !== 0 ? (
                    <>
                        <div className="my-2">
                            {matchedCount} of the imported sales have matched with existing sale uploads. Do you want
                            the matching sales be overridden, or ignored?
                        </div>
                        <div className="buttons">
                            <Button
                                classes={[ "button-cancel" ]}
                                onClick={() => {
                                    dispatch(hideDialog());
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                classes={[ "button-save" ]}
                                onClick={() => {
                                    void onFinaliseImport(false);
                                }}
                            >
                                Override
                            </Button>
                            <Button
                                classes={[ "button-save" ]}
                                onClick={() => {
                                    void onFinaliseImport(true);
                                }}
                            >
                                Ignore
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="my-2">
                            Do you wish to continue the import?
                        </div>
                        <div className="buttons">
                            <Button
                                classes={[ "button-cancel" ]}
                                onClick={() => {
                                    dispatch(hideDialog());
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                classes={[ "button-save" ]}
                                onClick={() => {
                                    void onFinaliseImport(false);
                                }}
                            >
                                Continue
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div>
            {content}
        </div>
    );
};
